export type IntegrationStatus =
  | "active"
  | "provisioning"
  | "requested"
  | "available";

// IP-152: live OAuth connection state. Null = the user has never clicked
// Connect for this integration on this tenant. The non-null values mirror
// tenant_integration_connections.status server-side.
//   pending      - connect in flight (OAuth tab open / API key validating)
//   active       - connected and usable
//   error        - a real auth attempt failed upstream
//   revoked      - access pulled upstream
//   disconnected - IP-206: user soft-deleted the connection (reconnect re-runs
//                  the connect flow; the row is kept for history)
export type ConnectionStatus =
  | "pending"
  | "active"
  | "error"
  | "revoked"
  | "disconnected";

// Normalised provider auth_type values that drive the connect affordance.
// Mirrors ztc-core's AuthType (Kotlin) and the V36 `configured` SQL rule.
export const AUTH_TYPE = {
  apiKey: "api_key",
  oauth2: "oauth2",
} as const;

// IP-171: one concrete function the agent gains from an integration, shown
// on the detail page. `name` + `description` are the provider's own (e.g.
// Composio's) display name and one-line summary, served verbatim from the
// elected provider via /api/public/integrations/{slug}.
export type IntegrationTool = {
  slug: string;
  name: string;
  description: string | null;
};

// The public settings API supports allow/deny; ask remains reserved internally.
export type ToolPermission = "allow" | "deny";

// One explicit per-tool rule. Presence in `rules` IS the explicit flag — the
// overlay no longer re-projects the full catalog tool list, so a tool with no
// rule here simply inherits `defaultPermission`.
export type ToolPermissionRule = {
  toolSlug: string;
  permission: ToolPermission;
};

// Lean rule overlay for a connected integration: the integration-level default
// plus only the tenant's explicit per-tool overrides. Mirrors ztc-core's
// ToolPermissionsView.
export type ToolPermissionsView = {
  canEdit: boolean;
  slug: string;
  defaultPermission: ToolPermission;
  rules: ToolPermissionRule[];
};

// IP-247: one connected account for an integration on this tenant. A tenant can
// hold several. `alias` is null for the primary/unlabeled account (the card then
// shows the integration title); `connectionId` addresses per-card reconnect /
// disconnect / cancel.
export type Connection = {
  connectionId: string;
  alias: string | null;
  status: ConnectionStatus;
  // Which provider this connection is for ("composio" = business app,
  // "whatsapp" = chat channel). Lets the detail page keep a channel connection
  // out of the Business app card. Optional so older cached payloads (pre-field)
  // degrade as a business-app connection (the prior behaviour).
  provider?: string;
  // The connected WhatsApp number for a channel connection; null otherwise.
  accountLabel?: string | null;
};

// Splits an integration's connections into its chat-channel connection(s) and
// its business-app connection(s) so the detail page can show each in its own
// card. A channel connection is one whose `provider` matches the integration's
// `channel` (e.g. provider "whatsapp" === channel "whatsapp"); everything else
// (the Composio business app, etc.) is a business-app connection. With no
// channel, every connection is a business-app connection (unchanged behaviour).
export const partitionConnections = (
  connections: Connection[],
  channel: string | null | undefined,
): { channel: Connection[]; businessApp: Connection[] } => {
  const channelConns: Connection[] = [];
  const businessAppConns: Connection[] = [];
  for (const c of connections) {
    if (channel != null && c.provider === channel) channelConns.push(c);
    else businessAppConns.push(c);
  }
  return { channel: channelConns, businessApp: businessAppConns };
};

// The number(s) to display for a connected channel: the account labels of the
// active channel connections that carry one. A channel can have more than one
// (e.g. several paired WhatsApp numbers), so this returns all of them.
export const activeChannelNumbers = (channelConnections: Connection[]): string[] =>
  channelConnections
    .filter((c) => c.status === "active")
    .map((c) => c.accountLabel)
    .filter((n): n is string => n != null && n.length > 0);

// IP-205: one required credential field for an API-key connect, from the
// integration's stored schema. The connect modal renders one input per field;
// `name` is the key the value is submitted under, `displayName` is the label.
export type ApiKeyField = {
  name: string;
  displayName: string;
  type: string;
  description: string | null;
  // Hand-seeded fields only: the value the form starts with, and placeholder text.
  defaultValue?: string | null;
  placeholder?: string | null;
  // Sits beside the previous field in the form (a port next to its server).
  inlineWithPrevious?: boolean | null;
};

// V49: caller-specific availability, computed server-side. THE field the
// detail page keys its connect affordances and degradation notice on.
//   available   - caller can connect ("Ready to go")
//   restricted  - deliberately gated for this caller, nothing broken
//                 ("Request access", NO degradation notice)
//   unavailable - not connectable, possibly broken ("Request access" + the
//                 service notice when an active connection exists)
export type IntegrationAvailability = "available" | "restricted" | "unavailable";

// Mirrors ztc-core's assertConnectable: Reconnect / Add another only work while
// the integration is available to the caller or a connection is still live
// (active or error). A disconnected-only tenant gets "Request access" alone.
export const canStartConnect = (
  availability: IntegrationAvailability,
  connections: Connection[],
): boolean =>
  availability === "available" || connections.some((c) => c.status === "active" || c.status === "error");

export type Integration = {
  slug: string;
  title: string;
  description: string;
  category: string;
  iconBrandHex?: string;
  featured: boolean;
  status: IntegrationStatus;
  included: boolean;
  monthlyCost: number;
  oneTimeCost: number;
  connectionStatus: ConnectionStatus | null;
  // IP-247: every connected account for this tenant + integration. Empty before
  // the user connects anything; optional so mocks/fixtures can omit it (fromApi
  // always sets it). `connectionStatus` above stays as the primary's status.
  connections?: Connection[];
  // How the customer connects this integration (V33). 'oauth' = the default
  // gateway Connect flow; 'guided' = a custom step-by-step setup wizard
  // (Telegram). Drives whether the detail page opens the guided modal. Optional
  // (like logoUrl) so preview/mock fixtures can omit it; fromApi always sets it.
  setupKind?: string;
  // Messaging-channel capability (V20260615), orthogonal to setupKind. Non-null
  // ('whatsapp') = the detail page renders a guided chat-channel setup panel
  // ABOVE the normal connect flow. null/absent for every integration without
  // one. Optional so mocks/fixtures can omit it; fromApi always sets it.
  channel?: string | null;
  // auth_type of the elected provider ('oauth2', 'api_key', ...) or null.
  // Drives which connect affordance the detail page renders.
  authType: string | null;
  // V49: caller-specific availability (see IntegrationAvailability). Optional
  // so mocks/fixtures can omit it; fromApi always sets it (falling back to a
  // `configured`-derived value for older payloads).
  availability?: IntegrationAvailability;
  // IP-205: derived server-side = (availability == available). False ->
  // Connect is hidden, customer sees Register interest + a "Request access"
  // badge.
  configured: boolean;
  // IP-205: the admin's raw connectability override. null = auto (follow
  // Composio), true = forced connectable, false = forced off. Admin-only;
  // drives which option the connectability menu shows as active.
  configuredOverride: boolean | null;
  // V49: true = connecting is restricted to platform admins while we test it
  // in production. Non-admins never receive a true value — the API downgrades
  // such entries to configured=false (visible, "Request access") — so this
  // only drives the admin UI's "Admins only" badge + menu state. Optional so
  // mocks/fixtures can omit it; fromApi always sets it.
  adminOnly?: boolean;
  // Brand logo. `ourLogoUrl` is the path we serve (/integration-logos/... in
  // local-dev); `logoUrl` is the upstream URL. The Logo component prefers
  // ourLogoUrl, then logoUrl, then a simple-icons glyph / monogram. Optional
  // (like iconBrandHex) so preview/mock fixtures can omit them.
  logoUrl?: string | null;
  ourLogoUrl?: string | null;
  // IP-422: true = Connect must first collect the customer's own OAuth app
  // credentials (self-serve BYO, e.g. their Meta Ads app) — the detail page
  // opens the credentials modal instead of redirecting. Optional so
  // mocks/fixtures can omit it; fromApi always sets it.
  byoOauthPrompt?: boolean;
  // IP-422: true = a tenant config is already registered. Connect goes the
  // plain OAuth route, but the page offers "use different app credentials"
  // so a config created from bad credentials can be replaced self-serve.
  byoOauthConfigured?: boolean;
};

type IntegrationApiResponse = {
  slug: string;
  title: string;
  description: string;
  category: string;
  iconBrandHex: string | null;
  featured: boolean;
  status: IntegrationStatus;
  included: boolean;
  monthlyCostCents: number;
  oneTimeCostCents: number;
  connectionStatus: ConnectionStatus | null;
  connections?: Connection[];
  setupKind: string;
  channel: string | null;
  authType: string | null;
  availability?: IntegrationAvailability;
  configured: boolean;
  configuredOverride: boolean | null;
  adminOnly?: boolean;
  logoUrl: string | null;
  ourLogoUrl: string | null;
  byoOauthPrompt?: boolean;
  byoOauthConfigured?: boolean;
};

export const fromApi = (raw: IntegrationApiResponse): Integration => ({
  slug: raw.slug,
  title: raw.title,
  description: raw.description,
  category: raw.category,
  ...(raw.iconBrandHex !== null ? { iconBrandHex: raw.iconBrandHex } : {}),
  featured: raw.featured,
  status: raw.status,
  included: raw.included,
  monthlyCost: raw.monthlyCostCents / 100,
  oneTimeCost: raw.oneTimeCostCents / 100,
  connectionStatus: raw.connectionStatus,
  // Default [] so older payloads / mocks without the field render no cards.
  connections: raw.connections ?? [],
  // Default 'oauth' so older payloads / mocks without the field behave as the
  // standard connect flow.
  setupKind: raw.setupKind ?? "oauth",
  // null when the integration has no chat-channel affordance (the common case).
  channel: raw.channel ?? null,
  authType: raw.authType ?? null,
  // Default true so older payloads / mocks without the field stay enabled.
  // Older payloads without availability: derive it from configured.
  availability: raw.availability ?? (raw.configured === false ? "unavailable" : "available"),
  configured: raw.configured ?? true,
  configuredOverride: raw.configuredOverride ?? null,
  adminOnly: raw.adminOnly === true,
  logoUrl: raw.logoUrl ?? null,
  ourLogoUrl: raw.ourLogoUrl ?? null,
  byoOauthPrompt: raw.byoOauthPrompt === true,
  byoOauthConfigured: raw.byoOauthConfigured === true,
});
